import dialogManager from '../components/DialogManager/DialogManager';
import { Select, useTheme, useColorMode, useToast, Button } from '@chakra-ui/react';

const HOST = window.location.hostname.includes('a-dec') ? process.env.REACT_APP_ADEC_API_ENDPOINT : process.env.REACT_APP_API_ENDPOINT
const CQAPI_MAX_RETRIES = 5;
const CQAPI_RETRY_BASE_DELAY_MS = 1000; // 0.5s base delay

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getParam = (p)=>{
  const newSearchParams = new URLSearchParams(window.location.search);
  return newSearchParams.get(p);
}

const CQAPI_FETCH_TIMEOUT_MS = 10000; // 10 seconds — adjust as you like

const fetchWithTimeout = (url, options = {}, timeout) => {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    fetch(url, { ...options, signal: controller.signal })
      .then((res) => {
        clearTimeout(timer);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};


const getUid = () => {
  const queryUid = getParam("_uid");
  if (queryUid) return queryUid;
  if (localStorage.getItem('user')){
    try {
      return JSON.parse(localStorage.getItem('user')).id;
    }
    catch (error) {
      console.log(error);
      return null;
    }
  }
  return null;
};

const getBid = ()=>{
  const queryBid = getParam('bid')
  if (queryBid) return queryBid;
  if (localStorage.getItem('business') && localStorage.getItem('user') && Auth?.user?.isPatient) {
    try {
      return JSON.parse(localStorage.getItem('business')).id;
    }
    catch (error){
      console.log(error);
      return null;
    }
  }
  return null;
};

import React from 'react';
import {useStripe, useElements, CardElement, Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import AgreementModal from "../components/DialogManager/AgreementModal";
import theme from '../theme';

// Your Stripe public key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PKEY);

const useOptions = () => {
  const theme = useTheme(); // Access the theme context
  const {
    colorMode
  } = useColorMode();

  return {
    style: {
      base: {
        color: colorMode === "light" ? theme.colors.gray[700] : theme.colors.gray[50],
        letterSpacing: "0.025em",
        backgroundColor: colorMode === "light" ? theme.colors.gray[200] : theme.colors.gray[600],
        lineHeight: '40px',
        fontSize: theme.fontSizes.md, // Use font size from the Chakra theme
        fontFamily: theme.fonts.body, // Use the font family from the Chakra theme
        '::placeholder': {
          color: theme.colors.gray[400], // Use a color from the Chakra theme for placeholders
        },
      },
      invalid: {
        color: theme.colors.red[500], // Use a color from the Chakra theme for invalid input
      },
    },
  }
};



const StripeElementsForm = ({onSuccess, onError, _uid, skipSave, bid}) => {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe has not initialized yet
      return;
    }

    const cardElement = elements.getElement(CardElement);
    const {error, paymentMethod} = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });

    if (error) {
      console.error(error);
      onError(error);
    } else {
      if (skipSave){
        onSuccess(paymentMethod);
        return
      }
      // Here, send the paymentMethod.id to your server
      try {
        const params = new URLSearchParams({
          ...(_uid ? {_uid} : {}), 
          ...(bid ? {bid} : {})
        });
        const response = await cqAPI.post(
          _uid ? `/setting/card?${params.toString()}` : `/setting/subscriptions/card?${params.toString()}`, 
          {
            paymentMethodId: paymentMethod.id
          }
        );

        if (response.type === 'ok') {
          onSuccess(paymentMethod);
        } else {
          toast({
            title: "Error",
            description: response.msg,
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      } catch (error) {
        onError(error);
      }
    }
  };

  const options = useOptions();

  return (
    <form onSubmit={handleSubmit}>
      <CardElement options={options}/>
      <Button type="submit" marginTop="40px" disabled={!stripe}>Save Card</Button>
    </form>
  );
};

class STREAMAPI{
  init(user, setUser){
    this.user = user;
    this.setUser = setUser
  }
  post(ep, body){
    const bid = getBid()
    const lambdaUrl = bid? `${HOST}${ep}?bid=${bid}` : `${HOST}${ep}`
    return fetch(lambdaUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.user && this.user.token ? {
          Authorization : `Bearer ${this.user.token}`
        } : {}
      },
      body: JSON.stringify(body || {})
    });
  }
}

class CQAPI {
  init(user, setUser) {
    this.user = user;
    this.setUser = setUser
  }
  async performRequest(method, endpoint, body, timeout) {
    const requestOptions = {
      headers: {
        ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...(this.user && this.user.token ? { Authorization: `Bearer ${this.user.token}` } : {})
      },
      method,
      withCredentials: true,
      ...(body
        ? body instanceof FormData
          ? { body }
          : { body: JSON.stringify(body) }
        : {}
      )
    };

    let lastError = null;

    for (let attempt = 0; attempt < CQAPI_MAX_RETRIES; attempt++) {
      try {
        let response;

        // Timeout ONLY for GET requests
        if (method.toLowerCase() === 'get' && timeout) {
          response = await fetchWithTimeout(`${HOST}${endpoint}`, requestOptions, timeout);
        } else {
          response = await fetch(`${HOST}${endpoint}`, requestOptions);
        }

        let jsonResponse;
        try {
          jsonResponse = await response.json();
        } catch (parseError) {
          jsonResponse = { type: 'error', msg: 'Unable to parse response' };
        }

        // If the response is OK or looks like a normal API error, just return it
        if (response.ok) {
          return jsonResponse;
        }

        // For non-OK HTTP status codes:
        // Retry only on 5xx (server) errors; return immediately on 4xx
        if (response.status >= 500 && response.status < 600) {
          lastError = new Error(
            `Server error ${response.status} on attempt ${attempt + 1}`
          );
        } else {
          return jsonResponse || {
            type: 'error',
            msg: `Request failed with status ${response.status}`,
            code: response.status
          };
        }
      } catch (error) {
        // Network / fetch-level error (what we expect from "spotty internet")
        lastError = error;
      }

      // If we're here, something failed. If we have retries left, back off then try again.
      if (attempt < CQAPI_MAX_RETRIES - 1) {
        const delay = CQAPI_RETRY_BASE_DELAY_MS * Math.pow(2, attempt); // 0.5s, 1s, 2s, 4s, ...
        await sleep(delay);
      }
    }

    // All retries exhausted
    return {
      type: 'error',
      msg: 'Network Error - Please try again later!'
    };
  }

    async downloadFile(ep){
      const requestOptions = {
        headers: {
          ...this.user && this.user.token ? { Authorization: `Bearer ${this.user.token}` } : {}
        },
        withCredentials: true,
        method: 'GET'
      };
      try {
        const endpoint = this.getEndpoint(ep)
        const response = await fetch(`${HOST}${endpoint}`,requestOptions)
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const blob = await response.blob()
        return window.URL.createObjectURL(blob)
      } catch (error) {
        return error.response; // Handle the error response
      }
    }

    async requestCreditCard({endpoint, skipSave, uid, bid}) {
      return new Promise((resolve, reject) => {
        const onSuccess = (response) => {
          dialogManager.close();
          resolve(response);
        };

        const _uid = uid || (endpoint && endpoint.includes('_uid=') && endpoint.split('_uid=')[1].split('&')[0]);
        //if _uid is passed, it means we are updating the user not the business.

        const onError = (error) => {
          console.error("Error saving card:", error);
          reject(error);
        };

        const formWithProvider = (
          <Elements stripe={stripePromise} >
            <StripeElementsForm onSuccess={onSuccess} onError={onError} _uid={_uid} bid={bid} skipSave={skipSave} />
          </Elements>
        );

        // Open the dialog
        dialogManager.open({
          body: formWithProvider,
          isClosable: true,
          onDeny: () => {
            resolve(null);
          }
        });
      });
    }

  async requestAgreement(data, callback) {
    return new Promise((resolve, reject) => {
      const onSuccess = async () => {
        dialogManager.close();
        try {
          const resp = await callback();
          return resolve(resp);
        } catch (e) {
          console.log(e)
        }
      };

      const onError = (error) => {
        console.error("Error accepting agreement:", error);
        reject(error);
      };

      // Open the agreement modal in the dialog manager
      dialogManager.open({
        body: <AgreementModal agreements={data} onDone={onSuccess} onError={onError}/>,
        isClosable: false,
        theme
      });
    });
  }


    async errorHandler(response, method, endpoint, body) {
      if (response.type === 'ok' &&  response.redir) {
          window.location.href = response.redir;
      }

      if (response && response.type == 'error') {
        // Handle specific error codes as needed
        if (response.code == 100) {
          this.setUser(null);
          return new Promise((resolve) => {
            const loginResolver = () => {
              resolve(this[method](endpoint, body));
            };
            Auth.onLogin(loginResolver);
          });
        }
        if (response.code == 6111) {
          const shouldRetry = await this.showRetryDialog(response.msg);
          if (shouldRetry) {
            return this[method](endpoint + (endpoint.includes('?')?'&':'?') +'paidcash=1', body); // Retry the request
          }else{
            return this[method](endpoint + (endpoint.includes('?')?'&':'?') +'paidcash=0', body); // Retry the request
          }
        }

        if (response.code == 1100) {
          const shouldRetry = await this.showRetryDialog(response.msg + ' Are you sure you want to continue?', response.header);
          if (shouldRetry) {
            return this[method](endpoint + (endpoint.includes('?')?'&':'?') +'confirm=1', body); // Retry the request
          }else{
            return false
          }
        }
        if (response.code == 6100){
          const storedBid = localStorage.getItem('bid')
          const l = storedBid || (await LocationSelectorManager.openLocationSelector().then(x=>x.bid))
          if (getBid() != l) {
            const newSearchParams = new URLSearchParams(window.location.search);
            newSearchParams.set("bid", l);
            window.location.replace(`${window.location.pathname}?${newSearchParams.toString()}`);
          }

          return this[method](endpoint, body);
        }
        if (response.code == 6015){
          //get credit card from the user
          try {
            const resp = await this.requestCreditCard({
              ...(response.bid? {bid : response.bid} : {})
            });
            if (!resp){
              return {type: 'error' , 'msg' : 'Please add a credit card to your account.'}
            }
            return this[method](endpoint, body);
          } catch (error) {
            console.error(`Unable to get credit card: ${error}`);
          }
        }
        if (response.code == 1015){
          //get credit card from the user
          try {
            const resp = await this.requestCreditCard({endpoint, ...response.uid? {uid : response.uid} : {}});
            if (!resp){
              return {type: 'error' , 'msg' : 'Please add a credit card to your account.'}
            }
            return this[method](endpoint, body);
          } catch (error) {
            console.error(`Unable to get credit card: ${error}`);
          }
        }
        if (response.code == 7001) {
          try {
            const resp = await this.requestAgreement(response.data, () => this[method](endpoint, body));
            if (!resp) {
              return { type: 'error', 'msg': 'Something Went Wrong!.' }
            }
            return this[method](endpoint, body);
          } catch (error) {
            console.error(`Unable to accept agreements: ${error}`);
          }
        }
      }

      return response;
    }


  async showRetryDialog(message, header = "") {
        return new Promise((resolve) => {
          dialogManager.open({
            onAccept: () => {resolve(true); return true},
            onDeny: () => resolve(false),
            acceptText: 'Yes',
            denyText: 'No',
            body: message,
            header: header || 'Confirmation needed',
            theme
          });
        });
      }

      getEndpoint(ep, urlParams ={}){
        const bid = getBid()
        const _uid = getUid()
        const requestURLparams = {
          ...urlParams,
          ...(bid && !ep.includes('bid=')? {bid} : {}),
          ...(_uid && !ep.includes('_uid=') ? {_uid} : {})
        };
        const encodedParams = new URLSearchParams(requestURLparams).toString();
        if (!encodedParams) {
          return ep;
        }

        const separator = ep.includes('?')?'&':'?';
        return `${ep}${separator}${encodedParams}`
      }

      async post(ep, body) {
        body = body || {}
        const endpoint = this.getEndpoint(ep);
        const response = await this.performRequest('post', endpoint, body);
        return this.errorHandler(response, 'post', endpoint, body);
      }

      async get(ep, urlParams= {},timeout=CQAPI_FETCH_TIMEOUT_MS) {
        const endpoint = this.getEndpoint(ep, urlParams);
        const response = await this.performRequest('get', endpoint, undefined, timeout);
        return this.errorHandler(response, 'get', endpoint);
      }
      async del(ep, body) {
        const endpoint = this.getEndpoint(ep);
        const response = await this.performRequest('delete', endpoint, body);
        return this.errorHandler(response, 'del', endpoint, body);
      }
      async put(ep, body) {
        const endpoint = this.getEndpoint(ep);
        const response = await this.performRequest('put', endpoint, body);
        return this.errorHandler(response, 'put', endpoint, body);
      }
  }


const cqAPI = new CQAPI()
const streamAPI = new STREAMAPI()

class AUTH {

  init(user, setUser) {
    this.queue = []
    this.user = user;
    this.setUser = setUser
    this.storeUser = (u) => {
      this.setUser(u);
      if (u) {
        localStorage.setItem("user", JSON.stringify(u));
      } else {
        localStorage.removeItem("user");
      }
    };
    streamAPI.init(this.user, this.setUser)
    cqAPI.init(this.user, this.setUser)
  }

  onLogin(p){
    this.queue.push(p)
  }

  async login(u){
    await Promise.all(this.queue)
  }

  currentAuthenticatedUser() {
    return cqAPI.get('/isloggedin')
  }

  signIn({username, password, trustDevice, rememberMeToken}) {
    return cqAPI.post('/login',{
      username,
      password,
      referrerHost : location.host,
      trustDevice: trustDevice,
      rememberMeToken: rememberMeToken
    });
  }

  signOut(user) {
    return cqAPI.get('/logout').then((x)=>{this.storeUser(null)})
  }

  /**
   * Initiate a password reset flow with the backend. If successful, the specified email will receive a password reset
   * link that a user must navigate to in order to complete the process.
   * @param email Email to initiate request for. Must belong to an existing user
   * @returns {Promise<Response>} Response object from API request.
   */
  requestPasswordReset(email) {
    return cqAPI.post('/forgot_password', { email });
  }
}

const Auth = new AUTH()

const loadPatients = async (inputValue, noFilter) => {
  try {
    const searchParams = new URLSearchParams({
      query:inputValue, 
      noFilter
    });
    const response = await cqAPI.get(
      `/findpatient?${searchParams.toString()}`
    );
    if (response.type === "ok") {
      return response.data
        .filter(
          (x) => noFilter || (x.adapters && x.adapters[x.bid])
        )
        .map((patient) => ({
          label: patient.profile.name,
          value: patient?.adapters && patient?.adapters[patient.bid],
          email: patient.email,
          dob : patient.profile.dob,
          _id : patient._id
        }));
    }
    return [];
  } catch (error) {
    console.error("Error searching patients:", error);
    return [];
  }
};

const loadProfiles = async (inputValue) => {
  try {
    const response = await cqAPI.get(
      `/a-dec/findProfiles?query=${inputValue}`
    );
    if (response.type === "ok") {
      return response.data
        .map((profile) => ({
          id: profile.id,
          name: profile.name,
          online: profile.online,
          adec_id: profile.adec_id,
          provider_id: profile.provider_id,
          profile_name: profile.profile_name
        }));
    }
  } catch (error) {
    console.error("Error searching profiles:", error);
    return [];
  }
}

const loadCustomers = async (inputValue) => {
  try {
    const response = await cqAPI.get(
      `/findLocation?query=${inputValue}`
    );
    if (response.type === "ok") {
      return response.data
        .map((location) => ({
          _id : location._id,
          name: location.name,
          created : location.created,
          start_date : location.start_date
        }));
    }
  } catch (error) {
    console.error("Error searching profiles:", error);
    return [];
  }
}

class LocationSelectorManager {
  static selectionPromise = null;
  static selectedLocation = null;

  static openLocationSelector = async () => {
    // If there's already a selected location, return it immediately.
    if (LocationSelectorManager.selectedLocation) {
      return Promise.resolve(LocationSelectorManager.selectedLocation);
    }

    // If a selection process is already ongoing, return the existing promise.
    if (LocationSelectorManager.selectionPromise) {
      return LocationSelectorManager.selectionPromise;
    }

    // Create a new promise for the selection process.
    LocationSelectorManager.selectionPromise = new Promise(async (resolve) => {
      const locations = await cqAPI.get('/locations?bid=').then(x => x.data);

      const handleLocationSelect = (event) => {
        // Find and set the selected location based on the event.
        LocationSelectorManager.selectedLocation = locations.find(location => location.bid === event.target.value);
      };

      const body = (
        <Select placeholder="Select location" onChange={handleLocationSelect}>
          {locations.map((location) => (
            <option key={location.bid} value={location.bid}>{location.name}</option>
          ))}
        </Select>
      );

      dialogManager.open({
        body: body,
        header: "Select a Location",
        acceptText: 'Select',
        onAccept: () => {
          // Ensure a location is selected before closing the dialog.
          if (!LocationSelectorManager.selectedLocation) {
            return false;
          }
          resolve(LocationSelectorManager.selectedLocation);
          return true
        },
        isClosable: false,
      });
    });

    return LocationSelectorManager.selectionPromise;
  };
}

const uploadString = async (content, fileType, name, _private=false) => {
  const formData = new FormData();
  const blob = new Blob([content], { type: fileType });
  formData.append("file", blob, name);
  return cqAPI.post(`/upload${_private? '?private=1' : ''}`, formData);
};

const getUserForPatient = (bid, patient_guid)=>cqAPI.post(`/pp/user`,{to : `${bid}.${patient_guid}`}).then(response=>(response.type === 'ok') && response.data)

const getProviderForProfile = (adec_id)=>cqAPI.get(`/a-dec/getProvider`,{adec_id}).then(response=>(response.type === 'ok') && response.data.provider_id)

const getIfGroupedByReplyStatus = () => {
  const raw = localStorage.getItem("user");
  const user = raw ? JSON.parse(raw) : null;
  return user?.inbox_grouped_by_reply_status ?? false;
};

export { Auth, cqAPI, streamAPI, getBid, LocationSelectorManager, HOST, loadPatients, uploadString, getUid, getParam, getUserForPatient, loadProfiles, getProviderForProfile, loadCustomers, getIfGroupedByReplyStatus };
